import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronRight, FileCheck, Loader2 } from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatPeriodForDisplay } from "@/lib/fiscalAgendaDisplay";
import {
  getMyAccountantRequests,
  type MyAccountantRequest,
} from "@/services/authService";

function relativeAge(iso: string, locale: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(iso));
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(new Date(iso));
}

export function AccountantRequestsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "es";

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "requestsAggregate"],
    queryFn: () => getMyAccountantRequests(["pending", "in_progress"]),
    refetchInterval: 60_000,
    enabled: Boolean(user?.id),
  });

  // Show pending before in_progress, then oldest first so the most stuck
  // requests bubble to the top of the queue.
  const sorted = useMemo(() => {
    return requests.slice().sort((a, b) => {
      if (a.status !== b.status) {
        if (a.status === "pending") return -1;
        if (b.status === "pending") return 1;
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [requests]);

  const open = (req: MyAccountantRequest): void => {
    // Pass the client tenantId via query string. The detail page reads
    // it and tells apiClient to stamp X-Tenant-Id on every call — the
    // backend AuthGuard validates the accountant's membership and
    // resolves tenant-scoped endpoints against this client. No more
    // silently swapping the persistent active tenant.
    navigate(
      `/accountant/requests/${req.id}?tenant=${encodeURIComponent(req.tenantId)}`,
    );
  };

  return (
    <div className="space-y-6 fx-content">
      <PageHeader
        title={t("accountant.requests.title", "Solicitudes")}
        description={t(
          "accountant.requests.description",
          "Pedidos pendientes en todos tus clientes. Las más antiguas primero.",
        )}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--fx-ink-500)]">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[var(--fx-r-md)] border border-[color:var(--fx-ink-200)] bg-[color:var(--fx-white)] px-6 py-16 text-center">
          <FileCheck
            className="h-10 w-10 text-[color:var(--fx-ink-300)]"
            aria-hidden
          />
          <p className="text-[color:var(--fx-ink-700)]">
            {t(
              "accountant.requests.empty",
              "No tenés solicitudes pendientes en este momento.",
            )}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden rounded-[var(--fx-r-md)] border border-[color:var(--fx-ink-200)] bg-[color:var(--fx-white)] md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("accountant.requests.col.client", "Cliente")}</TableHead>
                  <TableHead>{t("accountant.requests.col.obligation", "Obligación")}</TableHead>
                  <TableHead>{t("accountant.requests.col.dueDate", "Vencimiento")}</TableHead>
                  <TableHead>{t("accountant.requests.col.status", "Estado")}</TableHead>
                  <TableHead>{t("accountant.requests.col.requested", "Pedida")}</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((req) => (
                  <TableRow
                    key={req.id}
                    className="cursor-pointer"
                    onClick={() => open(req)}
                  >
                    <TableCell className="font-medium text-[color:var(--fx-ink-900)]">
                      <span className="flex items-center gap-2">
                        <Building2
                          className="h-4 w-4 text-[color:var(--fx-ink-400)]"
                          aria-hidden
                        />
                        {req.tenantName ??
                          t("accountant.home.unnamed", "Empresa sin nombre")}
                      </span>
                    </TableCell>
                    <TableCell>
                      {req.fiscalObligation
                        ? `${req.fiscalObligation.taxType} · ${formatPeriodForDisplay(
                            req.fiscalObligation.period,
                            locale,
                          )}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {req.fiscalObligation?.dueDate
                        ? formatDisplayDate(req.fiscalObligation.dueDate, { locale, useCalendarDate: true })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={req.status} />
                    </TableCell>
                    <TableCell className="text-[13px] text-[color:var(--fx-ink-500)]">
                      {relativeAge(req.createdAt, locale)}
                    </TableCell>
                    <TableCell>
                      <ChevronRight
                        className="h-4 w-4 text-[color:var(--fx-ink-400)]"
                        aria-hidden
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="grid gap-3 md:hidden">
            {sorted.map((req) => (
              <button
                key={req.id}
                type="button"
                onClick={() => open(req)}
                className="flex flex-col gap-2 rounded-[var(--fx-r-md)] border border-[color:var(--fx-ink-200)] bg-[color:var(--fx-white)] p-4 text-left transition hover:border-[color:var(--fx-accent-300)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 text-[14px] font-semibold text-[color:var(--fx-ink-900)]">
                    <Building2
                      className="h-4 w-4 shrink-0 text-[color:var(--fx-ink-400)]"
                      aria-hidden
                    />
                    <span className="truncate">
                      {req.tenantName ??
                        t("accountant.home.unnamed", "Empresa sin nombre")}
                    </span>
                  </span>
                  <StatusChip status={req.status} />
                </div>
                <div className="text-[13px] text-[color:var(--fx-ink-700)]">
                  {req.fiscalObligation
                    ? `${req.fiscalObligation.taxType} · ${formatPeriodForDisplay(req.fiscalObligation.period, locale)}`
                    : "—"}
                </div>
                <div className="flex items-center justify-between text-[12px] text-[color:var(--fx-ink-500)]">
                  <span>
                    {req.fiscalObligation?.dueDate
                      ? t("accountant.requests.due", {
                          date: formatDisplayDate(
                            req.fiscalObligation.dueDate,
                            { locale, useCalendarDate: true },
                          ),
                          defaultValue: "Vence {{date}}",
                        })
                      : ""}
                  </span>
                  <span>{relativeAge(req.createdAt, locale)}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusChip({
  status,
}: {
  status: MyAccountantRequest["status"];
}): JSX.Element {
  const { t } = useTranslation();
  const map: Record<MyAccountantRequest["status"], { label: string; cls: string }> = {
    pending: {
      label: t("accountant.requests.status.pending", "Pendiente"),
      cls:
        "bg-[color:var(--fx-warn-bg,rgba(201,94,62,0.12))] text-[color:var(--fx-warn,#C95E3E)]",
    },
    in_progress: {
      label: t("accountant.requests.status.inProgress", "En curso"),
      cls: "bg-[color:var(--fx-accent-100)] text-[color:var(--fx-accent-700)]",
    },
    completed: {
      label: t("accountant.requests.status.completed", "Completado"),
      cls: "bg-[color:var(--fx-paper-2)] text-[color:var(--fx-ink-700)]",
    },
  };
  const v = map[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${v.cls}`}>
      {v.label}
    </span>
  );
}
